import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { getNominative, showToast } from 'common/utils'
import { Home, ResidentBody, UserRole } from '@lmc/ts-common'
import {
  ProfileContent,
  ButtonsWrapper,
  ProfileWrapper
} from '../../styledComponents'
import { useEffect, useRef, useState } from 'react'
import { handleOnFinishFailed, scrollToTop } from '../../utils/utils'
import useResident, { convertFormTypeToResidentBody } from 'hooks/useResident'
import { Spinner } from 'components/generic'
import { ZoneFormCard } from './FormCards/Zone'
import { LocationFormCard } from './FormCards/Location'
import { CarersFormCard } from './FormCards/Carers'
import { useSelector } from 'react-redux'
import { generalConstants } from 'locale/general'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useRoleAccess } from 'components/AccessControl'

type FormType = {}
const ResidentSiteInformation = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const convertFormTypeToPartialResidentBody = (
    values: FormType
  ): Partial<ResidentBody> => ({
    ...values
  })

  const handleOnFinish = async (values: FormType) => {
    try {
      setIsUpdateLoading(true)
      const convertedValues = convertFormTypeToResidentBody(values, (vals) =>
        convertFormTypeToPartialResidentBody(vals)
      )
      setIsDirty(false)
      await updateResident(convertedValues)
      setIsUpdateLoading(false)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
      setIsUpdateLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (values: Partial<ResidentBody>) => {
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-site-info-loading` : 'site-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-site-info-no-resident` : 'site-info-no-resident'
        }
      />
    )
  }

  const initialValues: Partial<FormType> = {
    ...resident
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={testId ? `${testId}-site-info-wrapper` : 'site-info-wrapper'}
    >
      <PageHeader
        sticky
        title={t(supConstants.SITE_RELATED_INFORMATION)}
        data-testid={testId ? `${testId}-site-info-header` : 'site-info-header'}
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-site-info-buttons` : 'site-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId ? `${testId}-site-info-cancel-btn` : 'site-info-cancel-btn'
            }
          >
            {t(supConstants.CANCEL)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId ? `${testId}-site-info-save-btn` : 'site-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-site-info-content` : 'site-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form<FormType>
          form={form}
          onFinish={handleOnFinish}
          initialValues={initialValues}
          onValuesChange={handleFormValuesChange}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={testId ? `${testId}-site-info-form` : 'site-info-form'}
        >
          <ZoneFormCard resident={resident} testId={testId} />
          <LocationFormCard testId={testId} />
          <CarersFormCard resident={resident} form={form} testId={testId} />
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

export default ResidentSiteInformation
